import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import authService from '../services/AuthService';

const API_BASE = 'https://server.multisellerpro.com/api';
const DAILY_LIMIT = 10;

function getRemainingScans() {
    const today = new Date().toDateString();
    const stored = JSON.parse(localStorage.getItem('aip_free_scans') || '{}');
    if (stored.date !== today) return DAILY_LIMIT;
    return Math.max(0, DAILY_LIMIT - (stored.count || 0));
}

function consumeScan() {
    const today = new Date().toDateString();
    const stored = JSON.parse(localStorage.getItem('aip_free_scans') || '{}');
    const count = stored.date === today ? (stored.count || 0) + 1 : 1;
    localStorage.setItem('aip_free_scans', JSON.stringify({ date: today, count }));
}

const DEFAULT_PROMPT =
    'Make this a professional product photo for an online marketplace. Add a white background and make it look like an official Amazon, Walmart, or eBay product photo.';

export default function FreeProductPhotoMaker({ user }) {
    const isSubscriber = !!(
        user &&
        user.subscription_active &&
        user.subscription_plan &&
        user.subscription_plan !== 'No plan'
    );
    const [phase, setPhase] = useState('intro'); // intro | camera | preview | ai_prompt | ai_generating | ai_result | limit
    const [orientation, setOrientation] = useState('portrait'); // portrait | landscape
    const [facing, setFacing] = useState('environment');
    const [capturedFrame, setCapturedFrame] = useState(null);
    const [aiResult, setAiResult] = useState(null);
    const [prompt, setPrompt] = useState(() => {
        return localStorage.getItem('aip_ai_thumb_prompt') || DEFAULT_PROMPT;
    });
    const [promptHistory, setPromptHistory] = useState(() => {
        try { return JSON.parse(localStorage.getItem('aip_prompt_history') || '[]'); } catch { return []; }
    });
    const [showHistory, setShowHistory] = useState(false);
    const [error, setError] = useState('');
    const [remaining, setRemaining] = useState(() =>
        isSubscriber ? Infinity : getRemainingScans()
    );

    useEffect(() => {
        setRemaining(isSubscriber ? Infinity : getRemainingScans());
    }, [isSubscriber]);
    const [timer, setTimer] = useState(null);
    const [timerDuration, setTimerDuration] = useState(3);
    const [showTimerPicker, setShowTimerPicker] = useState(false);
    const [cameras, setCameras] = useState([]);
    const [showCamPicker, setShowCamPicker] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [aiPromptDraft, setAiPromptDraft] = useState('');
    const [uploadLoading, setUploadLoading] = useState(false);

    // ── permanent URL (for marketplaces that require a URL, not a file) ──
    const [permanentUrl, setPermanentUrl] = useState(null);
    const [gettingUrl, setGettingUrl] = useState(false);
    const [urlError, setUrlError] = useState('');
    const [copied, setCopied] = useState(false);

    const isLoggedIn = !!(user && user.email);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const previewLoopRef = useRef(null);
    const timerIntervalRef = useRef(null);
    const orientationRef = useRef('portrait');
    const facingRef = useRef('environment');

    // ── camera helpers ────────────────────────────────────────────────────────
    const stopStream = useCallback(() => {
        cancelAnimationFrame(previewLoopRef.current);
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
    }, []);

    const startPreviewLoop = useCallback(() => {
        cancelAnimationFrame(previewLoopRef.current);
        const draw = () => {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            if (video && canvas && video.readyState >= 2 && video.videoWidth > 0) {
                const orient = orientationRef.current;
                const outW = orient === 'landscape' ? 1280 : 720;
                const outH = orient === 'landscape' ? 720 : orient === 'square' ? 720 : 1280;
                if (canvas.width !== outW || canvas.height !== outH) {
                    canvas.width = outW;
                    canvas.height = outH;
                }
                const ctx = canvas.getContext('2d');
                const vR = video.videoWidth / video.videoHeight;
                const cR = outW / outH;
                let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;
                if (vR > cR) { sw = video.videoHeight * cR; sx = (video.videoWidth - sw) / 2; }
                else { sh = video.videoWidth / cR; sy = (video.videoHeight - sh) / 2; }
                ctx.save();
                if (facingRef.current === 'user') { ctx.translate(outW, 0); ctx.scale(-1, 1); }
                ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);
                ctx.restore();
            }
            previewLoopRef.current = requestAnimationFrame(draw);
        };
        draw();
    }, []);

    const openCamera = useCallback(async (faceMode) => {
        stopStream();
        setError('');
        const fm = faceMode ?? facingRef.current;
        facingRef.current = fm;
        setFacing(fm);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: fm, width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
            startPreviewLoop();
            const devices = await navigator.mediaDevices.enumerateDevices();
            setCameras(devices.filter(d => d.kind === 'videoinput'));
        } catch {
            setError('Camera access denied. Please allow camera permissions and try again.');
        }
    }, [stopStream, startPreviewLoop]);

    useEffect(() => {
        orientationRef.current = orientation;
    }, [orientation]);

    useEffect(() => {
        return () => { stopStream(); clearInterval(timerIntervalRef.current); };
    }, [stopStream]);

    // ── capture ───────────────────────────────────────────────────────────────
    const capturePhoto = useCallback(() => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        if (!canvas || !video || !video.videoWidth) return;
        const orient = orientationRef.current;
        const outW = orient === 'landscape' ? 1280 : 720;
        const outH = orient === 'landscape' ? 720 : orient === 'square' ? 720 : 1280;
        canvas.width = outW; canvas.height = outH;
        const ctx = canvas.getContext('2d');
        const vR = video.videoWidth / video.videoHeight;
        const cR = outW / outH;
        let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;
        if (vR > cR) { sw = video.videoHeight * cR; sx = (video.videoWidth - sw) / 2; }
        else { sh = video.videoWidth / cR; sy = (video.videoHeight - sh) / 2; }
        if (facingRef.current === 'user') {
            ctx.save(); ctx.translate(outW, 0); ctx.scale(-1, 1);
            ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH); ctx.restore();
        } else {
            ctx.drawImage(video, sx, sy, sw, sh, 0, 0, outW, outH);
        }
        const dataUrl = canvas.toDataURL('image/png');
        stopStream();
        setCapturedFrame(dataUrl);
        setPhase('preview');
    }, [stopStream]);

    const startTimer = () => {
        clearInterval(timerIntervalRef.current);
        let count = timerDuration;
        setTimer(count);
        timerIntervalRef.current = setInterval(() => {
            count--;
            if (count === 0) {
                clearInterval(timerIntervalRef.current);
                setTimer(null);
                capturePhoto();
            } else {
                setTimer(count);
            }
        }, 1000);
    };

    const cancelTimer = () => {
        clearInterval(timerIntervalRef.current);
        setTimer(null);
    };

    // ── AI generation ─────────────────────────────────────────────────────────
    const savePromptToHistory = (p) => {
        if (!p || !p.trim()) return;
        setPromptHistory(prev => {
            const next = [p, ...prev.filter(x => x !== p)].slice(0, 15);
            localStorage.setItem('aip_prompt_history', JSON.stringify(next));
            return next;
        });
    };

    const generate = async (overridePrompt, referenceImageOverride) => {
        if (!isSubscriber && remaining <= 0) { setPhase('limit'); return; }
        setGenerating(true);
        setError('');
        setPhase('ai_generating');
        const p = overridePrompt || prompt;
        localStorage.setItem('aip_ai_thumb_prompt', p);
        savePromptToHistory(p);
        try {
            const r = await fetch(`${API_BASE}/video/generate-ai-thumbnail-public`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    image_data: referenceImageOverride || capturedFrame,
                    prompt: p,
                    model: 'gpt',
                    width: orientation === 'landscape' ? 1280 : 720,
                    height: orientation === 'landscape' ? 720 : orientation === 'square' ? 720 : 1280,
                }),
            });
            if (!r.ok) throw new Error('Generation failed');
            const data = await r.json();
            if (!isSubscriber) {
                consumeScan();
                setRemaining(getRemainingScans());
            }
            setAiResult(data.thumbnail_url || data.image_url);
            setPermanentUrl(null);
            setUrlError('');
            setPhase('ai_result');
        } catch (e) {
            setError('Generation failed. Please try again.');
            setPhase('ai_prompt');
        } finally {
            setGenerating(false);
        }
    };

    const handleGetPermanentUrl = async () => {
        if (!isLoggedIn) {
            setUrlError('Please sign in to your MultiSellerPro account to get a permanent URL.');
            return;
        }
        setGettingUrl(true);
        setUrlError('');
        setCopied(false);
        try {
            const imgRes = await fetch(aiResult);
            const blob = await imgRes.blob();
            const formData = new FormData();
            formData.append('file', blob, 'product-photo.png');
            formData.append('prefix', 'productphoto');

            const res = await authService.authenticatedFetch(`${API_BASE}/upload/image`, {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Could not create a permanent URL.');
            setPermanentUrl(data.url);
        } catch (e) {
            setUrlError(e.message || 'Something went wrong getting your URL. Please try again.');
        } finally {
            setGettingUrl(false);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadLoading(true);
        const reader = new FileReader();
        reader.onload = (ev) => {
            setCapturedFrame(ev.target.result);
            setPhase('preview');
            setUploadLoading(false);
            stopStream();
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    // ── RENDER ────────────────────────────────────────────────────────────────
    return (
        <div style={styles.page}>
            <Helmet>
                <title>FREE Product Photo Maker by MultiSellerPro</title>
                <meta name="description" content="Generate professional product photos in 20 seconds. Free tool by Multi Seller Pro." />
                <meta property="og:title" content="FREE Product Photo Maker by MultiSellerPro" />
                <meta property="og:description" content="Take or upload a photo, get a professional product photos in 20 seconds. Free!" />
                <meta property="og:image" content="https://www.multisellerpro.com/freeaipthumbnail.png" />
                <meta property="og:url" content="https://www.multisellerpro.com/" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="FREE Product Photo Maker by MultiSellerPro" />
                <meta name="twitter:description" content="Take or upload a photo, get a professional product photos in 20 seconds. Free!" />
                <meta name="twitter:image" content="https://www.multisellerpro.com/freeaipthumbnail.png" />
            </Helmet>

            <style>{css}</style>

            <main style={styles.main}>
                {/* ── INTRO ── */}
                {phase === 'intro' && (
                    <div style={styles.introWrap} className="fade-in">
                        <div style={styles.sparkleRow}>✨ 🎬 ✨</div>
                        <h1 style={styles.heroTitle}>
                            <span style={styles.heroLine1}>Product Photo</span>
                            <span style={styles.heroLine2}>Maker</span>
                        </h1>
                        <div style={styles.heroBadge}><span style={{ cursor: "pointer" }} onClick={() => { localStorage.removeItem('aip_free_scans'); window.location.reload(); }}>🎁</span> FREE TOOL</div>
                        <p style={styles.heroSub}>
                            MultiSellerPro's <strong>Seller Pro</strong> giving you a free sneak peek of what we're capable of...
                        </p>
                        <p style={styles.heroSub2}>
                            Take or upload a photo of yourself with your product (or just the product), and we will generate a <strong>professional Product Photo</strong> in 20 seconds. Enjoy! 🚀
                        </p>

                        {isSubscriber ? (
                            <div style={styles.subscriberBanner}>
                                <div style={styles.subscriberBannerEmoji}>🎁</div>
                                <div>
                                    <div style={styles.subscriberBannerTitle}>UNLIMITED Photos!</div>
                                    <div style={styles.subscriberBannerSub}>As a subscriber, this is our gift to you!</div>
                                </div>
                            </div>
                        ) : (
                            <div style={styles.limitNote}>
                                {remaining} of {DAILY_LIMIT} free scans remaining today
                                <div style={styles.limitBar}>
                                    <div style={{ ...styles.limitFill, width: `${(remaining / DAILY_LIMIT) * 100}%` }} />
                                </div>
                            </div>
                        )}

                        <div style={styles.introBtns}>
                            <button style={styles.btnPrimary} onClick={() => { setPhase('camera'); openCamera('environment'); }}>
                                📸 Take a Photo
                            </button>
                            <label style={styles.btnSecondary}>
                                {uploadLoading ? '⏳ Loading…' : '⬆️ Upload Photo'}
                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />
                            </label>
                        </div>

                        {!isSubscriber && (
                            <p style={styles.proTeaser}>
                                Want unlimited? <a href="https://multisellerpro.com" target="_blank" rel="noreferrer" style={styles.proLink}>Subscribe to MSP Seller Pro →</a>
                            </p>
                        )}
                    </div>
                )}

                {/* ── CAMERA ── */}
                {phase === 'camera' && (
                    <div style={styles.cameraWrap} className="fade-in">
                        <div style={styles.camHeader}>
                            <button style={styles.backBtn} onClick={() => { stopStream(); cancelTimer(); setPhase('intro'); }}>← Back</button>
                            <span style={styles.camTitle}>📸 Take Product Photo</span>
                            <div style={{ width: 60 }} />
                        </div>

                        {/* Canvas preview */}
                        <div style={{
                            ...styles.camViewport,
                            aspectRatio: orientation === 'landscape' ? '16/9' : orientation === 'square' ? '1/1' : '9/16',
                        }}>
                            <video ref={videoRef} autoPlay playsInline muted style={{ display: 'none' }} />
                            <canvas ref={canvasRef} style={styles.camCanvas} />

                            {/* Timer overlay */}
                            {timer !== null && (
                                <div style={styles.timerOverlay}>
                                    <span style={styles.timerNum} key={timer}>{timer}</span>
                                </div>
                            )}

                            {/* Rec badge */}
                            <div style={styles.liveBadge}>● LIVE</div>
                        </div>

                        {error && <div style={styles.errBox}>{error}</div>}

                        {/* Controls */}
                        <div style={styles.camControls}>
                            {/* Row 1: orientation + facing */}
                            <div style={styles.controlRow}>
                                <button style={{ ...styles.ctrlBtn, ...(orientation === 'portrait' ? styles.ctrlActive : {}) }}
                                    onClick={() => { cancelTimer(); orientationRef.current = 'portrait'; setOrientation('portrait'); }}>
                                    ↕ Portrait
                                </button>
                                <button style={{ ...styles.ctrlBtn, ...(orientation === 'landscape' ? styles.ctrlActive : {}) }}
                                    onClick={() => { cancelTimer(); orientationRef.current = 'landscape'; setOrientation('landscape'); }}>
                                    ↔ Landscape
                                </button>
                                <button style={{ ...styles.ctrlBtn, ...(orientation === 'square' ? styles.ctrlActive : {}) }}
                                    onClick={() => { cancelTimer(); orientationRef.current = 'square'; setOrientation('square'); }}>
                                    ⬜ Square
                                </button>
                                <button style={styles.ctrlBtn} onClick={() => openCamera(facing === 'user' ? 'environment' : 'user')}>
                                    🔄 Flip
                                </button>
                                {cameras.length > 1 && (
                                    <button style={{ ...styles.ctrlBtn, ...(showCamPicker ? styles.ctrlActive : {}) }}
                                        onClick={() => setShowCamPicker(v => !v)}>
                                        🎥 Lens
                                    </button>
                                )}
                            </div>

                            {/* Camera picker */}
                            {showCamPicker && (
                                <div style={styles.picker}>
                                    {cameras.map((c, i) => (
                                        <button key={c.deviceId} style={styles.pickerItem}
                                            onClick={async () => {
                                                setShowCamPicker(false);
                                                stopStream();
                                                try {
                                                    const s = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: c.deviceId } }, audio: false });
                                                    streamRef.current = s;
                                                    if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play(); }
                                                    startPreviewLoop();
                                                } catch { setError('Could not open that camera.'); }
                                            }}>
                                            {c.label || `Camera ${i + 1}`}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Row 2: timer + capture */}
                            <div style={styles.controlRow}>
                                <div style={{ position: 'relative' }}>
                                    <button style={{ ...styles.ctrlBtn, ...(timer !== null ? styles.ctrlDanger : {}) }}
                                        onClick={() => {
                                            if (timer !== null) { cancelTimer(); return; }
                                            setShowTimerPicker(false);
                                            startTimer();
                                        }}>
                                        {timer !== null ? `⏱ ${timer}s` : `⏱ ${timerDuration}s`}
                                    </button>
                                    <button style={styles.ctrlSmall} onClick={() => setShowTimerPicker(v => !v)}>▾</button>
                                    {showTimerPicker && (
                                        <div style={{ ...styles.picker, bottom: '110%', left: 0, minWidth: 80 }}>
                                            {[3, 5, 10].map(s => (
                                                <button key={s} style={{ ...styles.pickerItem, fontWeight: timerDuration === s ? '800' : '500' }}
                                                    onClick={() => { setTimerDuration(s); setShowTimerPicker(false); cancelTimer(); }}>
                                                    {s}s
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button style={styles.captureBtn} onClick={() => { cancelTimer(); capturePhoto(); }}>
                                    📸 Capture
                                </button>
                                <label style={styles.ctrlBtn}>
                                    ⬆ Upload
                                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── PREVIEW ── */}
                {phase === 'preview' && capturedFrame && (
                    <div style={styles.previewWrap} className="fade-in">
                        <div style={styles.camHeader}>
                            <button style={styles.backBtn} onClick={() => { setCapturedFrame(null); setPhase('intro'); }}>← Back</button>
                            <span style={styles.camTitle}>Your Photo</span>
                            <div style={{ width: 60 }} />
                        </div>
                        <div style={styles.previewImgWrap}>
                            <img src={capturedFrame} alt="Preview" style={styles.previewImg} />
                        </div>
                        <div style={styles.previewBtns}>
                            <button style={styles.btnSecondary} onClick={() => { setCapturedFrame(null); setPhase('camera'); openCamera(facing); }}>
                                🔄 Retake
                            </button>
                            <label style={styles.btnSecondary}>
                                ⬆ New Photo
                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileUpload} />
                            </label>
                            <button style={styles.btnPrimary} onClick={() => setPhase('ai_prompt')}>
                                ✨ Generate AI Photo
                            </button>
                        </div>
                    </div>
                )}

                {/* ── AI PROMPT ── */}
                {phase === 'ai_prompt' && (
                    <div style={styles.promptWrap} className="fade-in">
                        <div style={styles.camHeader}>
                            <button style={styles.backBtn} onClick={() => setPhase('preview')}>← Back</button>
                            <span style={styles.camTitle}>✨ AI Prompt</span>
                            <div style={{ width: 60 }} />
                        </div>
                        {capturedFrame && <img src={capturedFrame} alt="Your photo" style={styles.promptThumb} />}
                        <div style={styles.remainingPill}>
                            {isSubscriber
                                ? '♾️ Unlimited generations — subscriber perk!'
                                : `${remaining} generation${remaining !== 1 ? 's' : ''} remaining today`
                            }
                        </div>
                        <p style={styles.promptLabel}>Output Photo:</p>
                        <div style={{ display: 'flex', gap: 8, padding: '0 16px 8px' }}>
                            <button
                                style={{ ...styles.ctrlBtn, ...(orientation === 'portrait' ? styles.ctrlActive : {}), flex: 1, justifyContent: 'center' }}
                                onClick={() => setOrientation('portrait')}
                            >
                                ↕ Vertical
                            </button>
                            <button
                                style={{ ...styles.ctrlBtn, ...(orientation === 'landscape' ? styles.ctrlActive : {}), flex: 1, justifyContent: 'center' }}
                                onClick={() => setOrientation('landscape')}
                            >
                                ↔ Horizontal
                            </button>
                            <button
                                style={{ ...styles.ctrlBtn, ...(orientation === 'square' ? styles.ctrlActive : {}), flex: 1, justifyContent: 'center' }}
                                onClick={() => setOrientation('square')}
                            >
                                ⬜ Square
                            </button>
                        </div>
                        <p style={styles.promptLabel}>Edit the prompt, then generate:</p>
                        <textarea
                            value={prompt}
                            onChange={e => setPrompt(e.target.value)}
                            rows={6}
                            style={styles.promptTextarea}
                        />
                        {promptHistory.length > 0 && (
                            <div style={{ padding: '6px 16px 0', position: 'relative' }}>
                                <button
                                    style={{ ...styles.ctrlBtn, color: 'rgba(255,255,255,0.7)', fontSize: 11 }}
                                    onClick={() => setShowHistory(v => !v)}
                                >
                                    🕓 Recent prompts {showHistory ? '▲' : '▼'}
                                </button>
                                {showHistory && (
                                    <div style={styles.historyDropdown}>
                                        {promptHistory.map((h, i) => (
                                            <button
                                                key={i}
                                                style={styles.historyItem}
                                                onClick={() => { setPrompt(h); setShowHistory(false); }}
                                            >
                                                {h.length > 80 ? h.slice(0, 80) + '…' : h}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {error && <div style={styles.errBox}>{error}</div>}
                        <div style={styles.promptBtns}>
                            <button style={styles.btnSecondary} onClick={() => setPrompt(DEFAULT_PROMPT)}>Reset</button>
                            <button
                                style={{ ...styles.btnPrimary, opacity: remaining <= 0 ? 0.5 : 1 }}
                                disabled={remaining <= 0}
                                onClick={() => generate(prompt)}
                            >
                                ✨ Generate
                            </button>
                        </div>
                        {!isSubscriber && remaining <= 0 && (
                            <div style={styles.limitMsg}>
                                You've used all {DAILY_LIMIT} free scans for today. Come back tomorrow or{' '}
                                <a href="https://multisellerpro.com" target="_blank" rel="noreferrer" style={styles.proLink}>subscribe to MSP Seller Pro!</a>
                            </div>
                        )}
                    </div>
                )}

                {/* ── AI PROMPT EDIT ── */}
                {phase === 'ai_prompt_edit' && (
                    <div style={styles.promptWrap} className="fade-in">
                        <div style={styles.camHeader}>
                            <button style={styles.backBtn} onClick={() => setPhase('ai_result')}>← Back</button>
                            <span style={styles.camTitle}>✏️ Edit Result</span>
                            <div style={{ width: 60 }} />
                        </div>
                        {aiResult && <img src={aiResult} alt="Current result" style={styles.promptThumb} />}
                        <div style={styles.remainingPill}>
                            {isSubscriber
                                ? '♾️ Unlimited generations — subscriber perk!'
                                : `${remaining} generation${remaining !== 1 ? 's' : ''} remaining today`
                            }
                        </div>
                        <p style={styles.promptLabel}>Describe what to change:</p>
                        <textarea
                            value={aiPromptDraft}
                            onChange={e => setAiPromptDraft(e.target.value)}
                            placeholder="e.g. 'Make the background white', 'Add more contrast', 'Change text to say BEST QUALITY'..."
                            rows={5}
                            style={styles.promptTextarea}
                            autoFocus
                        />
                        {error && <div style={styles.errBox}>{error}</div>}
                        <div style={styles.promptBtns}>
                            <button style={styles.btnSecondary} onClick={() => setPhase('ai_result')}>Cancel</button>
                            <button
                                style={{ ...styles.btnPrimary, opacity: remaining <= 0 ? 0.5 : 1 }}
                                disabled={remaining <= 0 || !aiPromptDraft.trim()}
                                onClick={() => {
                                    const editPrompt = `${prompt}\n\nEDIT INSTRUCTION: ${aiPromptDraft.trim()}. Keep the product unchanged, only modify what was asked.`;
                                    generate(editPrompt, aiResult);
                                }}
                            >
                                ✨ Apply Edit
                            </button>
                        </div>
                    </div>
                )}

                {/* ── GENERATING ── */}
                {phase === 'ai_generating' && (
                    <div style={styles.generatingWrap} className="fade-in">
                        <div style={styles.spinnerRing} />
                        <p style={styles.generatingTitle}>Creating your photo...</p>
                        <p style={styles.generatingSubtitle}>This takes about 20 seconds ☕</p>
                        {capturedFrame && <img src={capturedFrame} alt="" style={styles.generatingThumb} />}
                    </div>
                )}

                {/* ── AI RESULT ── */}
                {phase === 'ai_result' && aiResult && (
                    <div style={styles.resultWrap} className="fade-in">
                        <div style={styles.camHeader}>
                            <button style={styles.backBtn} onClick={() => setPhase('ai_prompt')}>← Edit</button>
                            <span style={styles.camTitle}>🎉 Your Photo!</span>
                            <div style={{ width: 60 }} />
                        </div>

                        <div style={styles.resultImgWrap}>
                            <img src={aiResult} alt="AI Photo" style={styles.resultImg} />
                        </div>

                        <div style={styles.resultBtns}>
                            <button style={styles.btnSecondary} onClick={() => { setAiResult(null); setPhase('ai_prompt'); }}>
                                🔄 Regenerate
                            </button>
                            <button style={styles.btnSecondary} onClick={() => { setAiPromptDraft(''); setPhase('ai_prompt_edit'); }}>
                                ✏️ Edit
                            </button>
                            <button style={styles.btnSecondary} onClick={() => { setCapturedFrame(null); setAiResult(null); setPhase('intro'); }}>
                                📸 New Photo
                            </button>
                            <button
                                style={styles.btnSecondary}
                                onClick={async () => {
                                    try {
                                        const res = await fetch(aiResult);
                                        const blob = await res.blob();
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'product-photo.png';
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                                    } catch {
                                        window.open(aiResult, '_blank');
                                    }
                                }}
                            >
                                ⬇ Download
                            </button>
                            <button
                                style={{ ...styles.btnPrimary, opacity: gettingUrl ? 0.7 : 1 }}
                                disabled={gettingUrl}
                                onClick={handleGetPermanentUrl}
                            >
                                {gettingUrl ? '⏳ Getting URL…' : '🔗 Get URL'}
                            </button>
                        </div>

                        {urlError && <div style={styles.errBox}>{urlError}</div>}

                        {permanentUrl && (
                            <div style={styles.urlResultBox}>
                                <p style={styles.urlResultLabel}>Your permanent image URL — paste this anywhere a marketplace asks for a photo URL:</p>
                                <div style={styles.urlResultRow}>
                                    <input readOnly value={permanentUrl} style={styles.urlResultInput}
                                        onFocus={(e) => e.target.select()} />
                                    <button
                                        style={styles.btnSecondary}
                                        onClick={() => {
                                            navigator.clipboard.writeText(permanentUrl);
                                            setCopied(true);
                                            setTimeout(() => setCopied(false), 2000);
                                        }}
                                    >
                                        {copied ? '✓ Copied' : '📋 Copy'}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div style={styles.remainingPill}>
                            {isSubscriber
                                ? '♾️ Unlimited generations — subscriber perk!'
                                : `${remaining} generation${remaining !== 1 ? 's' : ''} remaining today`
                            }
                        </div>

                        {!isSubscriber && remaining <= 0 && (
                            <div style={styles.limitMsg}>
                                You've used all your free scans for today! Come back tomorrow or unlock unlimited with MSP Seller Pro.
                            </div>
                        )}

                        {/* Upsell */}
                        {!isSubscriber && (
                            <div style={styles.upsellCard}>
                                <div style={styles.upsellEmoji}>🚀</div>
                                <div style={styles.upsellText}>
                                    <strong>Love it?</strong> MSP Seller Pro gives you unlimited photo generation, video recording, auto-posting to Amazon, YouTube uploads, eBay listing, all the Creator Connection tools you need & more.
                                </div>
                                <a href="https://multisellerpro.com" target="_blank" rel="noreferrer" style={styles.upsellBtn}>
                                    Unlock Seller Pro →
                                </a>
                            </div>
                        )}

                    </div>
                )}

                {/* ── LIMIT REACHED ── */}
                {phase === 'limit' && (
                    <div style={styles.limitWrap} className="fade-in">
                        <div style={styles.limitEmoji}>⏰</div>
                        <h2 style={styles.limitTitle}>You're on a roll!</h2>
                        <p style={styles.limitSubtitle}>
                            You've used all <strong>{DAILY_LIMIT} free scans</strong> for today.
                            Come back tomorrow for more, or unlock unlimited with Seller Pro!
                        </p>
                        <div style={styles.limitBtns}>
                            <button style={styles.btnSecondary} onClick={() => setPhase('intro')}>← Back</button>
                            <a href="https://multisellerpro.com" target="_blank" rel="noreferrer" style={styles.btnPrimary}>
                                🚀 Subscribe to Seller Pro
                            </a>
                        </div>
                        <p style={styles.limitReset}>Resets at midnight tonight 🌙</p>
                    </div>
                )}

            </main>

            {/* Footer */}
            <footer style={styles.footer}>
                <span>Made with ❤️ by </span>
                <a href="https://multisellerpro.com" target="_blank" rel="noreferrer" style={styles.footerLink}>MultiSellerPro</a>
                <span> · </span>
                <a href="https://multisellerpro.com" target="_blank" rel="noreferrer" style={styles.footerLink}>Get Seller Pro</a>
            </footer>
        </div>
    );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #f0f9ff 0%, #fefce8 50%, #fdf2f8 100%)',
        fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        top: 0,
        flexShrink: 0,
    },
    headerInner: {
        maxWidth: 640,
        margin: '0 auto',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    logo: { display: 'flex', alignItems: 'center', gap: 6 },
    logoMSP: {
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        color: 'white',
        fontWeight: 900,
        fontSize: 13,
        padding: '2px 7px',
        borderRadius: 6,
        letterSpacing: '-0.02em',
    },
    logoText: { fontWeight: 700, fontSize: 13, color: '#1e1b4b' },
    scanBadge: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
        border: '1px solid #f59e0b',
        borderRadius: 10,
        padding: '3px 10px',
    },
    scanNum: { fontWeight: 900, fontSize: 16, color: '#92400e', lineHeight: 1.1 },
    scanLabel: { fontSize: 9, color: '#92400e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },

    main: {
        flex: 1,
        maxWidth: 640,
        margin: '0 auto',
        width: '100%',
        padding: '0 0 24px',
    },

    // ── INTRO
    introWrap: {
        padding: '28px 20px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 12,
    },
    sparkleRow: { fontSize: 28, letterSpacing: 8 },
    heroTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
        margin: 0,
        lineHeight: 1.05,
    },
    heroLine1: {
        fontSize: 'clamp(28px, 8vw, 42px)',
        fontWeight: 900,
        color: '#1e1b4b',
        letterSpacing: '-0.03em',
    },
    heroLine2: {
        fontSize: 'clamp(34px, 10vw, 56px)',
        fontWeight: 900,
        background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        letterSpacing: '-0.04em',
        lineHeight: 1,
    },
    heroBadge: {
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        color: 'white',
        fontWeight: 800,
        fontSize: 11,
        padding: '3px 12px',
        borderRadius: 20,
        letterSpacing: '0.12em',
    },
    heroSub: {
        fontSize: 15,
        color: '#374151',
        maxWidth: 480,
        lineHeight: 1.6,
        margin: 0,
    },
    heroSub2: {
        fontSize: 14,
        color: '#6b7280',
        maxWidth: 460,
        lineHeight: 1.6,
        margin: 0,
    },
    statsRow: { display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' },
    statChip: {
        display: 'flex',
        gap: 5,
        alignItems: 'center',
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: 20,
        padding: '5px 14px',
        fontSize: 13,
        fontWeight: 600,
        color: '#374151',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    },
    limitNote: {
        fontSize: 12,
        color: '#6b7280',
        width: '100%',
        maxWidth: 300,
    },
    limitBar: {
        marginTop: 6,
        height: 6,
        background: '#e5e7eb',
        borderRadius: 3,
        overflow: 'hidden',
    },
    limitFill: {
        height: '100%',
        background: 'linear-gradient(90deg, #6366f1, #ec4899)',
        borderRadius: 3,
        transition: 'width 0.5s ease',
    },
    introBtns: { display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', width: '100%' },
    proTeaser: { fontSize: 13, color: '#9ca3af', margin: 0 },
    proLink: { color: '#6366f1', fontWeight: 700, textDecoration: 'none' },

    // ── BUTTONS
    btnPrimary: {
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        color: 'white',
        border: 'none',
        borderRadius: 14,
        padding: '13px 24px',
        fontWeight: 800,
        fontSize: 15,
        cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        transition: 'opacity 0.15s',
    },
    btnSecondary: {
        background: 'white',
        color: '#374151',
        border: '1.5px solid #e5e7eb',
        borderRadius: 14,
        padding: '12px 20px',
        fontWeight: 700,
        fontSize: 14,
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    },

    // ── CAMERA
    cameraWrap: {
        display: 'flex',
        flexDirection: 'column',
        height: '84vh',
        background: '#000',
    },
    camHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px',
        background: 'rgba(0,0,0,0.8)',
        flexShrink: 0,
    },
    backBtn: {
        background: 'rgba(255,255,255,0.15)',
        border: 'none',
        color: 'white',
        borderRadius: 8,
        padding: '6px 12px',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 600,
        width: 60,
    },
    camTitle: { color: 'white', fontWeight: 700, fontSize: 14 },
    camViewport: {
        flex: 1,
        position: 'relative',
        background: '#111',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 220px)',
    },
    camCanvas: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
    },
    timerOverlay: {
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
    },
    timerNum: {
        fontSize: 96,
        fontWeight: 900,
        color: 'white',
        textShadow: '0 0 48px rgba(99,102,241,0.9)',
        animation: 'popIn 0.3s ease',
    },
    liveBadge: {
        position: 'absolute',
        top: 8,
        left: 8,
        background: 'rgba(0,0,0,0.55)',
        color: '#f43f5e',
        fontSize: 10,
        fontWeight: 700,
        padding: '3px 8px',
        borderRadius: 20,
        letterSpacing: '0.08em',
    },
    camControls: {
        background: 'rgba(0,0,0,0.92)',
        padding: '10px 12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flexShrink: 0,
    },
    controlRow: {
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
    },
    ctrlBtn: {
        background: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.2)',
        color: 'white',
        borderRadius: 10,
        padding: '7px 12px',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
    },
    ctrlActive: {
        background: 'rgba(99,102,241,0.4)',
        borderColor: '#6366f1',
    },
    ctrlDanger: {
        background: 'rgba(239,68,68,0.3)',
        borderColor: '#ef4444',
    },
    ctrlSmall: {
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.15)',
        color: 'white',
        borderRadius: '0 10px 10px 0',
        padding: '7px 8px',
        fontSize: 11,
        cursor: 'pointer',
        marginLeft: -1,
    },
    captureBtn: {
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        border: 'none',
        color: 'white',
        borderRadius: 10,
        padding: '10px 22px',
        fontSize: 14,
        fontWeight: 800,
        cursor: 'pointer',
        boxShadow: '0 2px 12px rgba(99,102,241,0.4)',
    },
    picker: {
        position: 'absolute',
        bottom: '110%',
        left: 0,
        background: '#1e293b',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 10,
        overflow: 'hidden',
        zIndex: 20,
        minWidth: 160,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
    },
    pickerItem: {
        display: 'block',
        width: '100%',
        padding: '9px 14px',
        background: 'none',
        border: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        color: 'rgba(255,255,255,0.85)',
        fontSize: 12,
        cursor: 'pointer',
        textAlign: 'left',
        fontWeight: 500,
    },
    errBox: {
        margin: '8px 12px',
        background: 'rgba(239,68,68,0.1)',
        border: '1px solid rgba(239,68,68,0.3)',
        color: '#dc2626',
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 13,
    },

    // ── PREVIEW
    previewWrap: {
        display: 'flex',
        flexDirection: 'column',
        height: '83vh',
        background: '#0f172a',
    },
    previewImgWrap: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#000',
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 180px)',
    },
    previewImg: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        display: 'block',
    },
    previewBtns: {
        display: 'flex',
        gap: 8,
        padding: '12px',
        background: 'rgba(0,0,0,0.9)',
        flexWrap: 'wrap',
        justifyContent: 'center',
        flexShrink: 0,
    },

    // ── PROMPT
    promptWrap: {
        padding: '0 0 20px',
        display: 'flex',
        flexDirection: 'column',
        background: '#0f172a',
        minHeight: 'calc(100vh - 56px)',
    },
    promptThumb: {
        width: '100%',
        maxHeight: 200,
        objectFit: 'contain',
        background: '#000',
        display: 'block',
    },
    remainingPill: {
        alignSelf: 'center',
        background: 'rgba(255,255,255,0.08)',
        color: 'rgba(255,255,255,0.6)',
        fontSize: 11,
        fontWeight: 600,
        padding: '4px 12px',
        borderRadius: 20,
        margin: '8px 0 0',
    },
    promptLabel: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 12,
        margin: '10px 16px 4px',
        fontWeight: 600,
    },
    promptTextarea: {
        margin: '0 16px',
        padding: '10px 12px',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: 'white',
        borderRadius: 10,
        fontSize: 12,
        fontFamily: 'inherit',
        resize: 'vertical',
        outline: 'none',
        lineHeight: 1.6,
        minHeight: 120,
    },
    promptBtns: {
        display: 'flex',
        gap: 8,
        padding: '12px 16px 0',
        justifyContent: 'flex-end',
    },
    limitMsg: {
        margin: '10px 16px 0',
        padding: '10px 14px',
        background: 'rgba(239,68,68,0.1)',
        border: '1px solid rgba(239,68,68,0.2)',
        borderRadius: 10,
        color: '#fca5a5',
        fontSize: 13,
        lineHeight: 1.5,
    },

    // ── GENERATING
    generatingWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 56px)',
        gap: 16,
        background: '#0f172a',
        padding: 24,
    },
    spinnerRing: {
        width: 56,
        height: 56,
        border: '4px solid rgba(255,255,255,0.1)',
        borderTop: '4px solid #8b5cf6',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    generatingTitle: { color: 'white', fontWeight: 800, fontSize: 20, margin: 0, fontFamily: 'inherit' },
    generatingSubtitle: { color: 'rgba(255,255,255,0.45)', fontSize: 14, margin: 0, fontFamily: 'inherit' },
    generatingThumb: {
        width: 140,
        height: 140,
        objectFit: 'contain',
        borderRadius: 12,
        border: '2px solid rgba(255,255,255,0.1)',
        opacity: 0.5,
    },

    // ── RESULT
    resultWrap: {
        display: 'flex',
        flexDirection: 'column',
        background: '#0f172a',
        height: '100vh',
        paddingBottom: 24,
    },
    resultImgWrap: {
        flex: 1,
        position: 'relative',
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        maxHeight: 'calc(100vh - 280px)',
        overflow: 'hidden',
    },
    resultImg: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        display: 'block',
    },
    resultBadge: {
        position: 'absolute',
        top: 8,
        right: 8,
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        color: 'white',
        fontSize: 10,
        fontWeight: 700,
        padding: '3px 10px',
        borderRadius: 20,
        letterSpacing: '0.05em',
    },
    resultBtns: {
        display: 'flex',
        gap: 8,
        padding: '12px',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    upsellCard: {
        margin: '12px 16px 0',
        background: 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))',
        border: '1px solid rgba(99,102,241,0.25)',
        borderRadius: 16,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        textAlign: 'center',
    },
    upsellEmoji: { fontSize: 28 },
    upsellText: { color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 1.6 },
    urlResultBox: {
        margin: '4px 16px 0',
        padding: '12px 14px',
        background: 'rgba(99,102,241,0.08)',
        border: '1px solid rgba(99,102,241,0.25)',
        borderRadius: 12,
    },
    urlResultLabel: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 12,
        margin: '0 0 8px',
        lineHeight: 1.4,
    },
    urlResultRow: {
        display: 'flex',
        gap: 8,
    },
    urlResultInput: {
        flex: 1,
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.15)',
        color: 'white',
        borderRadius: 10,
        padding: '9px 12px',
        fontSize: 12,
        fontFamily: 'monospace',
        outline: 'none',
        minWidth: 0,
    },

    // ── LIMIT
    limitWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 56px)',
        padding: '32px 24px',
        gap: 12,
        textAlign: 'center',
        background: 'linear-gradient(160deg, #f0f9ff 0%, #fefce8 100%)',
    },
    limitEmoji: { fontSize: 56 },
    limitTitle: { fontSize: 28, fontWeight: 900, color: '#1e1b4b', margin: 0 },
    limitSubtitle: { fontSize: 15, color: '#6b7280', maxWidth: 340, lineHeight: 1.6, margin: 0 },
    limitBtns: { display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', marginTop: 4 },
    limitReset: { fontSize: 12, color: '#9ca3af', margin: 0 },

    // ── FOOTER
    footer: {
        padding: '14px 16px',
        textAlign: 'center',
        fontSize: 12,
        color: '#9ca3af',
        borderTop: '1px solid #e5e7eb',
        background: 'rgba(255,255,255,0.7)',
    },
    footerLink: { color: '#6366f1', fontWeight: 600, textDecoration: 'none' },
    historyDropdown: {
        position: 'absolute',
        top: '110%',
        left: 16,
        right: 16,
        background: '#1e293b',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 10,
        overflow: 'hidden',
        zIndex: 30,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        maxHeight: 260,
        overflowY: 'auto',
    },
    historyItem: {
        display: 'block',
        width: '100%',
        padding: '9px 14px',
        background: 'none',
        border: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        color: 'rgba(255,255,255,0.8)',
        fontSize: 12,
        cursor: 'pointer',
        textAlign: 'left',
        fontWeight: 400,
        lineHeight: 1.4,
        fontFamily: 'inherit',
    },
    subscriberBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
        border: '2px solid #f59e0b',
        borderRadius: 14,
        padding: '12px 18px',
        width: '100%',
        maxWidth: 320,
    },
    subscriberBannerEmoji: { fontSize: 28 },
    subscriberBannerTitle: {
        fontWeight: 900,
        fontSize: 15,
        color: '#78350f',
        letterSpacing: '-0.01em',
    },
    subscriberBannerSub: {
        fontSize: 12,
        color: '#92400e',
        marginTop: 2,
    },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; }
  body { margin: 0; padding: 0; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes popIn { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  .fade-in { animation: fadeIn 0.3s ease forwards; }
  button:hover { opacity: 0.88; }
  a:hover { opacity: 0.85; }
`;